import React, { useRef, useState, useMemo } from 'react';
import { ResponsiveBar } from '@nivo/bar';
import { FaSlidersH, FaTimes } from 'react-icons/fa';
import ChartCard, { ChartCardIconButton, ChartCardButton } from '../shared/ChartCard.js';
import SegmentedToggle from '../shared/SegmentedToggle.js';
import { buildExportSvg, downloadSvg } from '../shared/svgExport.js';
import { FONT_STACK, DARK_TEXT, MUTED_TEXT, formatPercent } from '../shared/chartTheme.js';
import '../../styles/DashboardComponents.css';

// Apparence fixe du mode Interactif (même look qu'avant l'introduction du mode Export,
// pas de notion de réglages ici). Le mode Export pilote les mêmes champs via `settings`.
// Couleur/arrondi/largeur par défaut alignés sur le reste du dashboard (palette
// désaturée partagée, coins peu arrondis, graphique compact plutôt qu'étiré pleine
// largeur — cf. UsageDonutChartNivo/les sunbursts).
const INTERACTIVE_APPEARANCE = {
    barColor: '#3E6B8A', // bleu ardoise, indépendant de la palette des diagrammes circulaires
    padding: 0.55,
    cornerRadius: 0,
    fontSize: 13,
    rowHeight: 34,
    chartWidth: 560,
    showPercent: false,
    roundPercent: false,
};

const DEFAULT_SETTINGS = { ...INTERACTIVE_APPEARANCE };

// Marge haute réservée au total (TotalLayer) : deux lignes de texte + un peu d'air
// avant la première barre.
const TOP_MARGIN = 56;

const SETTINGS_SECTIONS = [
    {
        id: 'dimensions',
        title: 'Dimensions',
        fields: [
            { key: 'chartWidth', label: 'Largeur du graphique', type: 'range', min: 320, max: 900, step: 20, unit: 'px' },
            { key: 'rowHeight', label: 'Hauteur des lignes', type: 'range', min: 24, max: 48, step: 2, unit: 'px' },
        ],
    },
    {
        id: 'bars',
        title: 'Barres',
        fields: [
            { key: 'barColor', label: 'Couleur des barres', type: 'color' },
            { key: 'padding', label: 'Espacement entre les barres', type: 'range', min: 0.1, max: 0.8, step: 0.05 },
            { key: 'cornerRadius', label: 'Arrondi des coins', type: 'range', min: 0, max: 20, step: 1, unit: 'px' },
        ],
    },
    {
        id: 'typography',
        title: 'Typographie',
        fields: [{ key: 'fontSize', label: 'Taille de police', type: 'range', min: 10, max: 20, step: 1, unit: 'px' }],
    },
    {
        id: 'content',
        title: 'Contenu affiché',
        fields: [
            { key: 'showPercent', label: 'Afficher les pourcentages', type: 'checkbox' },
            { key: 'roundPercent', label: 'Arrondir les pourcentages', type: 'checkbox' },
        ],
    },
];

const SettingsField = ({ field, settings, onChange }) => (
    <div className="sunburst-settings-row" key={field.key}>
        <label htmlFor={`person-chart-setting-${field.key}`}>{field.label}</label>
        {field.type === 'range' ? (
            <div className="sunburst-settings-range">
                <input
                    id={`person-chart-setting-${field.key}`}
                    type="range"
                    min={field.min}
                    max={field.max}
                    step={field.step}
                    value={settings[field.key]}
                    onChange={(e) => onChange(field.key, Number(e.target.value))}
                />
                <span className="sunburst-settings-value">
                    {settings[field.key]}
                    {field.unit}
                </span>
            </div>
        ) : field.type === 'color' ? (
            <input
                id={`person-chart-setting-${field.key}`}
                type="color"
                className="sunburst-settings-color"
                value={settings[field.key]}
                onChange={(e) => onChange(field.key, e.target.value)}
            />
        ) : (
            <label className="sunburst-settings-toggle">
                <input
                    id={`person-chart-setting-${field.key}`}
                    type="checkbox"
                    checked={settings[field.key]}
                    onChange={(e) => onChange(field.key, e.target.checked)}
                />
                <span className="sunburst-settings-toggle-slider" />
            </label>
        )}
    </div>
);

const SettingsPanel = ({ settings, onChange, onClose }) => (
    <div className="sunburst-settings-panel">
        <div className="sunburst-settings-panel-header">
            <span>Réglages d'affichage</span>
            <button type="button" className="sunburst-settings-close" onClick={onClose} aria-label="Fermer">
                <FaTimes />
            </button>
        </div>
        <div className="sunburst-settings-grid">
            {SETTINGS_SECTIONS.map((section) => (
                <div className="sunburst-settings-section" key={section.id}>
                    <div className="sunburst-settings-section-title">{section.title}</div>
                    {section.fields.map((field) => (
                        <SettingsField key={field.key} field={field} settings={settings} onChange={onChange} />
                    ))}
                </div>
            ))}
        </div>
    </div>
);

// Labels de fin de barre : Nivo n'a pas de position de label "hors barre" nativement
// pour un bar chart horizontal — layer custom, comme pour les connecteurs des
// sunbursts. On lit `bar.data.formattedValue` (déjà passé par `valueFormat`) plutôt que
// `bar.data.data.value` : Nivo construit `bar.data.data` en filtrant les clés "falsy"
// du datum brut, donc une valeur exactement à 0 y disparaît et fait planter un
// `.toFixed()` sur `undefined` (cf. incident déjà rencontré sur ce même chart).
const EndLabelsLayer = ({ bars, appearance }) => (
    <g>
        {bars.map((bar) => (
            <text
                key={bar.key}
                x={bar.x + bar.width + 8}
                y={bar.y + bar.height / 2}
                dominantBaseline="central"
                style={{ fontSize: appearance.fontSize, fontWeight: 600, fontFamily: FONT_STACK, fill: DARK_TEXT }}
            >
                {bar.data.formattedValue}
                {appearance.showPercent &&
                    typeof bar.data.data.percentage === 'number' &&
                    ` · ${formatPercent(bar.data.data.percentage, appearance.roundPercent)}%`}
            </text>
        ))}
    </g>
);

// Total (somme de toutes les barres affichées), en <text> SVG — comme le total central
// des donuts/sunbursts (CenterLayer), rendu en SVG plutôt qu'en overlay HTML pour être
// capturé par l'export SVG (buildExportSvg ne clone que le <svg> lui-même). Centré au-
// dessus des barres, dans la marge haute réservée à cet effet (cf. margin.top).
// Nivo rend chaque layer (natif ou custom) à l'intérieur d'un <g> déjà translaté de
// (margin.left, margin.top) : les coordonnées ci-dessous sont donc LOCALES à ce repère,
// pas absolues au canevas SVG — d'où le -margin.top pour remonter dans la marge haute
// réservée (sinon le total atterrit à la hauteur de la 1ère barre, cf. incident vécu
// sur CostPerUsageCard.js, resté discret ici car les barres courtes ne chevauchent pas
// le centre, mais le même décalage existait bel et bien).
const TotalLayer = ({ width, margin, total, unit, appearance }) => {
    const centerX = (width - margin.left - margin.right) / 2;
    const topY = -margin.top;
    const valueSize = Math.max(16, appearance.fontSize + 5);
    return (
        <g style={{ pointerEvents: 'none' }}>
            <text
                x={centerX}
                y={topY + 18}
                textAnchor="middle"
                dominantBaseline="central"
                style={{ fontSize: valueSize, fontWeight: 700, fontFamily: FONT_STACK, fill: DARK_TEXT }}
            >
                {total.toFixed(1)} {unit}
            </text>
            <text
                x={centerX}
                y={topY + 36}
                textAnchor="middle"
                dominantBaseline="central"
                style={{ fontSize: 10, fontWeight: 600, fontFamily: FONT_STACK, fill: MUTED_TEXT, letterSpacing: '0.03em' }}
            >
                TOTAL
            </text>
        </g>
    );
};

/* ─────────────────────────────────────────────
   "Consommation par personne" (ou par toute autre unité choisie) — même patron
   Interactif/Export que les autres graphiques du dashboard, mais entièrement
   autonome : mode, répartition usage/sous-usage, dénominateur personnalisé et
   réglages d'apparence restent internes (rien à partager avec un composant frère,
   contrairement au donut et son détail par point d'eau).

   @param {{id:string, label:string, value:number}[]} categoryTotals
   @param {{id:string, categoryId:string, label:string, value:number}[]} subcategoryTotals
   @param {number} personCount
───────────────────────────────────────────── */
const ConsumptionPerPersonCard = ({ categoryTotals, subcategoryTotals, personCount }) => {
    const containerRef = useRef(null);
    const [mode, setMode] = useState('interactive'); // 'interactive' | 'export'
    const [filter, setFilter] = useState('usage'); // 'usage' | 'subcategory'
    const [denominatorMode, setDenominatorMode] = useState('person'); // 'person' | 'custom'
    const [customCount, setCustomCount] = useState('');
    const [customUnitLabel, setCustomUnitLabel] = useState('');
    const [settings, setSettings] = useState(DEFAULT_SETTINGS);
    const [panelOpen, setPanelOpen] = useState(false);
    const [downloadState, setDownloadState] = useState('idle');

    const updateSetting = (key, value) => setSettings((prev) => ({ ...prev, [key]: value }));
    const appearance = mode === 'export' ? settings : INTERACTIVE_APPEARANCE;

    const hasCustomDenominator = denominatorMode === 'custom' && Number(customCount) > 0 && customUnitLabel.trim() !== '';
    const unitLabel = hasCustomDenominator ? customUnitLabel.trim() : 'personne';
    const valueUnit = hasCustomDenominator ? `L/${customUnitLabel.trim()}` : 'L/pers/j';

    // Même désambiguïsation "Autres (Catégorie)" que UsageTaxonomySunburstCard.js — un
    // bucket "Autres" existe une fois par catégorie et partage toujours ce libellé ; si
    // ça arrive pour plus d'une catégorie il faut le qualifier pour rester unique.
    const categoryLabelById = useMemo(
        () => Object.fromEntries(categoryTotals.map((c) => [c.id, c.label])),
        [categoryTotals]
    );
    const ambiguousSubcategoryLabels = useMemo(() => {
        const counts = {};
        subcategoryTotals.forEach((s) => {
            counts[s.label] = (counts[s.label] || 0) + 1;
        });
        return new Set(Object.keys(counts).filter((label) => counts[label] > 1));
    }, [subcategoryTotals]);
    const qualifiedLabel = (item) =>
        item.categoryId && ambiguousSubcategoryLabels.has(item.label)
            ? `${item.label} (${categoryLabelById[item.categoryId] || ''})`
            : item.label;

    const totals = filter === 'usage' ? categoryTotals : subcategoryTotals;
    const totalVolumeM3 = totals.reduce((sum, t) => sum + t.value, 0);
    const effectivePersonCount = personCount || 1; // même filet de sécurité que le calcul back (person_count || 1)

    const chartData = useMemo(
        () =>
            totals
                .map((t) => {
                    const volumeLiters = t.value * 1000;
                    const value = hasCustomDenominator
                        ? volumeLiters / Number(customCount)
                        : volumeLiters / (365 * effectivePersonCount);
                    const percentage = totalVolumeM3 > 0 ? (t.value / totalVolumeM3) * 100 : 0;
                    return { label: qualifiedLabel(t), value, percentage };
                })
                // Croissant : en layout horizontal, Nivo dessine la première barre en bas,
                // donc la plus forte consommation se retrouve en haut.
                .sort((a, b) => a.value - b.value),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [totals, hasCustomDenominator, customCount, effectivePersonCount, totalVolumeM3, ambiguousSubcategoryLabels, categoryLabelById]
    );

    const rowHeight = appearance.rowHeight;
    const totalValue = chartData.reduce((sum, d) => sum + d.value, 0);
    const chartHeight = chartData.length * rowHeight + TOP_MARGIN;

    const handleDownloadSvg = () => {
        const svgEl = containerRef.current?.querySelector('svg');
        if (!svgEl) return;
        downloadSvg(buildExportSvg(svgEl), 'consommation-par-personne.svg');
        setDownloadState('downloaded');
        setTimeout(() => setDownloadState('idle'), 2000);
    };

    const modeToggle = (
        <SegmentedToggle
            ariaLabel="Mode d'affichage du graphique"
            value={mode}
            onChange={setMode}
            options={[
                { value: 'interactive', label: 'Interactif' },
                { value: 'export', label: 'Export' },
            ]}
        />
    );

    return (
        <ChartCard
            title={`Consommation par ${unitLabel}`}
            actions={
                mode === 'export' ? (
                    <>
                        {modeToggle}
                        <ChartCardIconButton
                            onClick={() => setPanelOpen((v) => !v)}
                            title="Réglages d'affichage"
                            aria-label="Réglages d'affichage"
                        >
                            <FaSlidersH />
                        </ChartCardIconButton>
                        <ChartCardButton onClick={handleDownloadSvg} title="Télécharger le graphique en SVG">
                            {downloadState === 'downloaded' ? 'SVG téléchargé' : 'Télécharger en SVG'}
                        </ChartCardButton>
                    </>
                ) : (
                    modeToggle
                )
            }
            panel={mode === 'export' && panelOpen && <SettingsPanel settings={settings} onChange={updateSetting} onClose={() => setPanelOpen(false)} />}
        >
            <div className="chart-settings-bar">
                <div className="chart-settings-bar-group">
                    <p>Répartir par</p>
                    <SegmentedToggle
                        variant="primary"
                        ariaLabel="Répartir par"
                        value={filter}
                        onChange={setFilter}
                        options={[
                            { value: 'usage', label: 'Usage' },
                            { value: 'subcategory', label: 'Sous-usage' },
                        ]}
                    />
                </div>
                <div className="chart-settings-bar-group">
                    <p>Dénominateur</p>
                    <SegmentedToggle
                        variant="primary"
                        ariaLabel="Dénominateur"
                        value={denominatorMode}
                        onChange={setDenominatorMode}
                        options={[
                            { value: 'person', label: 'Personnes' },
                            { value: 'custom', label: 'Personnalisé' },
                        ]}
                    />
                </div>
                {denominatorMode === 'custom' && (
                    <div className="chart-settings-bar-group">
                        <input
                            type="number"
                            min="0"
                            className="chart-settings-bar-input"
                            placeholder="Nombre"
                            value={customCount}
                            onChange={(e) => setCustomCount(e.target.value)}
                        />
                        <input
                            type="text"
                            className="chart-settings-bar-input chart-settings-bar-input--unit"
                            placeholder="Unité (ex. lits)"
                            value={customUnitLabel}
                            onChange={(e) => setCustomUnitLabel(e.target.value)}
                        />
                    </div>
                )}
            </div>

            {chartData.length === 0 ? null : (
                <div className="consumption-gauge-container" ref={containerRef} style={{ width: appearance.chartWidth, height: chartHeight }}>
                    <ResponsiveBar
                        data={chartData}
                        keys={['value']}
                        indexBy="label"
                        layout="horizontal"
                        margin={{ top: TOP_MARGIN, right: 110, bottom: 0, left: 140 }}
                        padding={appearance.padding}
                        colors={() => appearance.barColor}
                        borderRadius={appearance.cornerRadius}
                        enableGridX={false}
                        enableGridY={false}
                        axisBottom={null}
                        axisLeft={{ tickSize: 0, tickPadding: 8 }}
                        enableLabel={false}
                        isInteractive={true}
                        animate={false}
                        valueFormat={(v) => `${v.toFixed(1)} ${valueUnit}`}
                        layers={[
                            'grid',
                            'axes',
                            'bars',
                            (p) => <EndLabelsLayer {...p} appearance={appearance} />,
                            (p) => <TotalLayer {...p} total={totalValue} unit={valueUnit} appearance={appearance} />,
                            'totals',
                            'markers',
                            'legends',
                            'annotations',
                        ]}
                        theme={{
                            text: { fontFamily: FONT_STACK },
                            axis: { ticks: { text: { fontSize: appearance.fontSize, fill: DARK_TEXT, fontFamily: FONT_STACK } } },
                        }}
                        tooltip={({ data: row, formattedValue }) => (
                            <div className="sunburst-tooltip">
                                <div className="sunburst-tooltip-label">{row.label}</div>
                                <div className="sunburst-tooltip-value">
                                    {formattedValue}
                                    {typeof row.percentage === 'number' && ` (${row.percentage.toFixed(1)}% du total)`}
                                </div>
                            </div>
                        )}
                    />
                </div>
            )}
        </ChartCard>
    );
};

export default ConsumptionPerPersonCard;
